import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import type {DirectTurnSurface} from '@github-ui/ahp-session-client/contracts'
import {
  clientInteractionAccepted,
  clientInteractionFailed,
  clientInteractionForEvent,
  clientInteractionPending,
  prepareLegacyClientInteraction,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {useCopilotClientInteractions} from '@github-ui/copilot-browser-latency/use-copilot-client-interactions'
import {useGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {copilotChatTextAreaId} from '@github-ui/copilot-chat-helpers/constants'
import {referencesAreEqual} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage, type SavedMessageSource} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {useIsCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {
  type CopilotChatCommandType,
  type CopilotChatReference,
  type CopilotChatRepo,
  type ImageReference,
  type ModeSupport,
  ModeSupportName,
  type ThreadScopedFileReference,
} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {ChatInputRef as SharedChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from '@github-ui/copilot-shared-ui/utils/portal-roots'
import type {TaskComposerTargetSelection} from '@github-ui/copilot-shared-ui/utils/task-composer-target-selection'
import {useSpaceSelection} from '@github-ui/copilot-spaces/hooks/use-space-selection'
import {sendEvent} from '@github-ui/hydro-analytics'
import {EnvironmentStrip} from '@github-ui/one-copilot-composer/environment-strip/EnvironmentStrip'
import {
  EnvironmentAttachmentControls,
  EnvironmentImageAttachments,
} from '@github-ui/one-copilot-composer/EnvironmentAttachments'
import {TaskChatInputToolbar} from '@github-ui/one-copilot-composer/task-chat-input-toolbar/TaskChatInputToolbar'
import type {EnvironmentSubmit} from '@github-ui/one-copilot-composer/use-environment-selection'
import {testIdProps} from '@github-ui/test-id-props'
import type {CommandEvent, CommandId} from '@github-ui/ui-commands'
import {CommandButton, CommandIconButton, ScopedCommands} from '@github-ui/ui-commands'
import {useMediaQuery} from '@github-ui/use-media-query'
import {useNavigate} from '@github-ui/use-navigate'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import {useSyntheticChange} from '@github-ui/use-synthetic-change'
import type {AnchorSide} from '@primer/behaviors'
import {PaperAirplaneIcon, PlusIcon, SquareFillIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Label, useSafeTimeout} from '@primer/react'
import {clsx} from 'clsx'
import type {RefObject} from 'react'
import {memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {useAddMentionReferences} from '../hooks/use-add-mention-references'
import {useAddUrlReferences} from '../hooks/use-add-url-references'
import {type AvailableCommand, useAvailableCommands} from '../hooks/use-available-commands'
import {isAgentCommand, useChatInput} from '../hooks/use-chat-input'
import {usePrettifiedBlocks} from '../hooks/use-prettified-blocks'
import type {CustomModelPickerProps} from '../plugin/copilot-immersive-plugin'
import {isIncompleteSlashCommand} from '../utils/chat-commands-helpers'
import {CopilotImageAttacher} from '../utils/copilot-image-attacher'
import {sendVisionErrorEvent} from '../utils/copilot-image-helpers'
import {CLIPBOARD_MIME_TYPE, CopilotTextAttacher, looksLikeCode} from '../utils/copilot-text-attacher'
import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {AttachmentMenu} from './AttachmentMenu'
import type {AttachmentMenuPanelType} from './AttachmentMenuPanelType'
import {ChatInputAutocomplete} from './autocomplete/ChatInputAutocomplete'
import styles from './ChatInput.module.css'
import {ChatInputPreview} from './ChatInputPreview'
import {ChatInputReferences} from './ChatInputReferences'
import {ChatModelControls} from './ChatModelControls'
import {ChatReposPicker} from './ChatReposPicker'
import {FullScreenDragOverlay} from './FullScreenDragOverlay'
import {MeterButton} from './MeterButton'
import {PrettifiedBlocksList} from './PrettifiedBlocksList'

/** When the user pastes text that is longer than this many characters, we will automatically convert it to a file. */
const PASTE_TEXT_AS_FILE_THRESHOLD = 1000

const LIMITED_KEYBINDING_COMMAND_IDS: CommandId[] = [
  'github:cancel',
  'copilot-chat:send-message',
  'copilot-chat:edit-last-message',
]

const START_TASK_BUTTON_TEXT = 'Start task'

async function observeLegacyCommand(
  interaction: NonNullable<Parameters<typeof clientInteractionPending>[0]>,
  run: () => Promise<boolean | void>,
) {
  clientInteractionPending(interaction)
  try {
    if ((await run()) === false) clientInteractionFailed(interaction)
    else clientInteractionAccepted(interaction)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') interaction.unresolved('cancelled')
    else clientInteractionFailed(interaction)
    throw error
  }
}

/**
 * The input handle moved down into `@github-ui/copilot-shared-ui` so the task
 * composer below this package can accept the same handle. It stays exported
 * here for the chat consumers that already read it from this module.
 */
export type ChatInputRef = SharedChatInputRef

export interface ChatInputProps {
  className?: string
  disableAutoFocus?: boolean
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  containerRef?: React.RefObject<HTMLFormElement | null>
  /**
   * Call `onAccepted` once the submission transport accepts the message. Return
   * `false` when the submission is rejected without throwing.
   */
  onSubmit?: (
    text: string,
    additionalReferences?: CopilotChatReference[],
    onAccepted?: () => void,
  ) => Promise<boolean | void>
  isStreaming?: boolean
  activeModeName?: string
  modes: [ModeSupport, ...ModeSupport[]]
  onClickMode?: (modeName: string) => void
  size?: 'small' | 'large'
  onSelectReference?: (reference: CopilotChatReference, event?: React.MouseEvent<HTMLAnchorElement>) => void
  onPromoteImplicitReference?: (reference: CopilotChatReference) => void
  onNewThreadSelected?: () => Promise<void>
  getReferenceVersion?: (reference: CopilotChatReference) => number | undefined
  /** Set of previously generated/referenced file names in this thread, to use as a reference for generating new file names. */
  existingFileNames?: Set<string>
  disabled?: boolean
  submitDisabled?: boolean
  extraToolbar?: React.ReactNode
  initialReference?: CopilotChatReference
  onRemoveReference?: (reference: CopilotChatReference) => void
  isNewConversation?: boolean
  onChatCommandChange?: (command: CopilotChatCommandType | null) => void
  /** Set to true when rendered in a Portal to handle height issues */
  isInPortal?: boolean
  shouldSubmitWithModifier?: boolean
  shouldClearTextOnCommandSubmit?: boolean
  shouldClearTextOnSubmit?: boolean
  slashCommandsEnabled?: boolean
  /** Extra command keys to highlight in the input preview without registering as slash commands */
  highlightCommands?: CopilotChatCommandType[]
  attachmentMenuSide?: AnchorSide
  storageKeySuffix?: string
  showStartTaskButton?: boolean
  customModelPicker?: (props: CustomModelPickerProps) => React.ReactNode
  initialText?: string
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
  /** Maximum height for the input in pixels. Applied as an inline `max-height` that overrides the CSS default cap on the input container. */
  maxTextareaHeight?: number
  formatTextAreaAriaLabel?: (defaultLabel: string | undefined) => string | undefined
  /** Maximum character length for the prompt. When set, enforces the limit using the maxLength attribute. */
  maxPromptLength?: number
  /** Source of the saved message, used to differentiate messages from different experiences */
  source?: SavedMessageSource
  /**
   * Optional custom paste handler. When provided, replaces the default paste handling
   * entirely (including text-to-file conversion and URL reference replacement).
   */
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>
  /**
   * Optional drag event handlers to spread onto the form element for inline
   * drag-and-drop support (e.g. image drops scoped to the input area).
   */
  dropTargetProps?: {
    onDragEnter: React.DragEventHandler
    onDragOver: React.DragEventHandler
    onDragLeave: React.DragEventHandler
    onDrop: React.DragEventHandler
  }
  /**
   * Optional callback invoked whenever the textarea text changes.
   * Useful for parent components that need to observe the input text without controlling it.
   */
  onTextChange?: (text: string) => void
  /**
   * When true, removes the explicit disabling of auto-correction and spell check on the textarea,
   * allowing browser defaults to apply. Useful for natural-language inputs (e.g. agent task prompts)
   * on mobile devices.
   */
  enableAutoCorrect?: boolean
  onEnvironmentSubmit?: EnvironmentSubmit
  supportsManagedSandbox?: boolean
  directTurnTelemetrySurface?: DirectTurnSurface
}

const ChatInputImpl = (props: ChatInputProps) => {
  const {plan, tokenBasedBillingEnabled} = useEntitlement()
  const state = useChatStateValues(
    'currentUserLogin',
    'defaultRecipient',
    'isWaitingOnAttachment',
    'attachmentProcessingType',
    'isWaitingOnCopilot',
    'messages',
    'mode',
    'model',
    'selectedThreadID',
    'slashCommandLoading',
  )
  const {model} = state
  const getChatState = useGetChatState()
  const manager = useChatManager()
  const maxMessagesReached = manager.maxMessagesReached()
  const [pastedFile, setPastedFile] = useState<File | null>(null)
  const {
    blocks: prettifiedBlocks,
    addBlock: addPrettifiedBlock,
    removeBlock: removePrettifiedBlock,
    updateBlockCode: updatePrettifiedBlockCode,
    toggleCollapse: togglePrettifiedBlockCollapse,
    clearBlocks: clearPrettifiedBlocks,
    getBlocksText,
  } = usePrettifiedBlocks()
  const slashCommandLoading = state.slashCommandLoading.state === 'loading'
  const {safeSetTimeout} = useSafeTimeout()
  const [codeBlockStatus, setCodeBlockStatus] = useState('')
  const internalContainerRef = useRef<HTMLFormElement>(null)

  const {
    onSelectReference,
    onSubmit,
    shouldClearTextOnCommandSubmit = true,
    shouldClearTextOnSubmit = true,
    slashCommandsEnabled = false,
    shouldSubmitWithModifier = false,
    extraToolbar,
    highlightCommands,
  } = props
  const containerRef = props.containerRef ?? internalContainerRef

  // prepend any prettified code blocks to the submitted text
  const wrappedOnSubmit = useCallback(
    async (submitText: string, additionalReferences?: CopilotChatReference[], event?: Event) => {
      const blocksText = getBlocksText()
      const finalText = [blocksText, submitText].filter(Boolean).join('\n')
      if (blocksText) clearPrettifiedBlocks()
      const interaction = onSubmit && finalText.trim() ? clientInteractionForEvent(event) : undefined
      prepareLegacyClientInteraction(interaction, finalText)
      let accepted = false
      const onAccepted = () => {
        if (accepted) return
        accepted = true
        clientInteractionAccepted(interaction)
      }
      try {
        const outcome = await onSubmit?.(finalText, additionalReferences, onAccepted)
        if (outcome === false) clientInteractionFailed(interaction)
        else onAccepted()
      } catch (error) {
        clientInteractionFailed(interaction)
        throw error
      }
    },
    [getBlocksText, clearPrettifiedBlocks, onSubmit],
  )
  const pastedFileNamePromise = useRef<Promise<string> | null>(null)
  const pastedFileName = useRef<string>('')
  const convertToFileShownLogged = useRef(false)
  const navigate = useNavigate()
  const {availableCommands, availableCommandKeys} = useAvailableCommands({enabled: slashCommandsEnabled})
  const allCommandKeys = highlightCommands?.length
    ? [...availableCommandKeys, ...highlightCommands]
    : availableCommandKeys
  const commandModes = availableCommands.map(cmd => cmd.mode).filter((mode): mode is ModeSupport => mode !== undefined)
  const chatModes = commandModes.length > 0 ? commandModes : props.modes

  // Store the selected mode NAME (not the object) and derive the object from `chatModes` during
  // render below. This keeps the active mode pointing at the current `chatModes` entry without an
  // effect re-syncing it on every render (which previously cascaded when `modes`/`chatModes` had an
  // unstable identity).
  const [selectedModeName, setSelectedModeName] = useState<string>(() => {
    if (props.activeModeName) {
      const activeModeFromProps = chatModes.find(m => m.name === props.activeModeName)
      if (activeModeFromProps) return activeModeFromProps.name
    }
    const savedModeName = copilotLocalStorage.getActiveMode()
    if (savedModeName) {
      const savedMode = chatModes.find(m => m.name === savedModeName)
      if (savedMode) return savedMode.name
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return chatModes[0]!.name
  })

  const onModeSelect = useCallback(
    (mode: ModeSupport, autoComplete?: boolean) => {
      props.onClickMode?.(mode.name)
      if (!props.activeModeName) {
        setSelectedModeName(mode.name)
      }
      copilotLocalStorage.setActiveMode(mode.name)
      sendEvent('dotcom_chat.activate', {
        target: 'MODE_SWITCH',
        mode: mode.name,
        autoComplete,
      })
    },
    [props],
  )

  // Use custom hook to manage space selection logic
  const {activeCopilotSpaceId} = useSpaceSelection()

  // Derive the active mode object from the current `chatModes`. `activeModeName` controls the
  // selection when provided (and valid); otherwise the internal `selectedModeName` does. Because it
  // is derived during render it always references the latest `chatModes` object and needs no
  // synchronizing effect.
  const controlledMode = props.activeModeName ? chatModes.find(m => m.name === props.activeModeName) : undefined
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMode = controlledMode ?? chatModes.find(m => m.name === selectedModeName) ?? chatModes[0]!

  const onClickMode = useCallback(
    (modeName: string) => {
      const mode = chatModes.find(m => m?.name === modeName)
      if (mode) {
        onModeSelect(mode)
      }
    },
    [chatModes, onModeSelect],
  )

  const addedInitialReference = useRef<CopilotChatReference | undefined>(undefined)

  // Add initial reference to manager
  useEffect(() => {
    if (!props.initialReference) return
    if (referencesAreEqual(addedInitialReference.current, props.initialReference)) return
    manager.addReference(props.initialReference, 'page-file-reference')
    addedInitialReference.current = props.initialReference
  }, [props.initialReference, manager])

  const clearPastedFile = useCallback(
    (logDismiss = false) => {
      pastedFileNamePromise.current = null
      pastedFileName.current = ''
      convertToFileShownLogged.current = false
      if (pastedFile) {
        setPastedFile(null)
        if (logDismiss) {
          sendEvent('dotcom_chat.activate', {target: 'CONVERT_TO_FILE_DISMISS', mode: state.mode})
        }
      }
    },
    [pastedFile, state.mode],
  )

  const {
    typingHasStarted,
    textAreaRef,
    textareaPreviewRef,
    textAreaPreviewContainerRef,
    text,
    setText,
    isAgentTaskSubmitInProgress,
    handleSubmit,
    handleSubmitToNewThread,
    handleScroll,
    handleChange,
    handleStop,
    handleFocus,
    command,
    setLastUsedRepo,
    // agent tasks
    selectedRepo,
    setSelectedRepo,
    selectedBranch,
    setSelectedBranch,
    selectedAgent,
    setSelectedAgent,
    selectedAgentModelSelection,
    setSelectedAgentModelSelection,
    initialRepoNWO,
    selectedEnvironment,
    setSelectedEnvironment,
    isAhpEnvironment,
    isSandboxEnvironment,
    isSandboxPolicyPending,
    showSandboxOption,
    showEnvironmentPicker,
    hasSendableReferences,
    environmentAttachments,
  } = useChatInput({
    isLoading: state.isWaitingOnCopilot || state.isWaitingOnAttachment || slashCommandLoading,
    onSubmit: wrappedOnSubmit,
    canSubmitWithEmptyText: prettifiedBlocks.length > 0,
    textAreaRef: props.textAreaRef,
    clearPastedFile,
    onChatCommandChange: props.onChatCommandChange,
    isInPortal: props.isInPortal,
    shouldClearTextOnCommandSubmit,
    shouldClearTextOnSubmit,
    availableCommands,
    activeModeName: currentMode?.name,
    storageKeySuffix: props.storageKeySuffix,
    initialText: props.initialText,
    maxTextareaHeight: props.maxTextareaHeight,
    source: props.source,
    getAdditionalSubmitText: getBlocksText,
    clearAdditionalSubmitContent: clearPrettifiedBlocks,
    onEnvironmentSubmit: props.onEnvironmentSubmit,
    supportsManagedSandbox: props.supportsManagedSandbox,
    directTurnTelemetrySurface: props.directTurnTelemetrySurface,
  })
  // Tracks textarea focus to drive the form outline, replacing `.chatForm:has(.input:focus)`
  // to avoid WebKit `:has()` invalidation (scoped to the textarea, not toolbar buttons).
  const [isInputFocused, setIsInputFocused] = useState(false)

  // Auto-switch to agent mode when the user types the `/agent ` shorthand, stripping the prefix.
  // Handled here on the change event rather than in an effect watching `text`, so it stays a direct
  // reaction to user input and doesn't need a set-state-in-effect.
  const handleChangeWithAgentSwitch = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      handleChange(e)
      const value = e.target.value
      if (!value.startsWith('/agent ')) return
      const agentCommand = availableCommands.find(cmd => cmd.key === 'agent')
      if (agentCommand?.mode && agentCommand.mode !== currentMode) {
        onModeSelect(agentCommand.mode, true)
        setText(value.slice(7)) // Remove '/agent ' from the beginning
      }
    },
    [handleChange, availableCommands, currentMode, onModeSelect, setText],
  )

  // Notify parent when text changes
  const {onTextChange} = props
  useEffect(() => {
    onTextChange?.(text)
  }, [text, onTextChange])

  // Get custom model picker from the active slash command if available, or use the prop
  const activeCommand = useMemo(
    () => availableCommands.find(cmd => cmd.key === command || cmd.mode?.name === currentMode.name),
    [availableCommands, command, currentMode],
  )
  const customModelPicker = activeCommand?.customModelPicker ?? props.customModelPicker

  const environmentStripTargetSelection = useMemo<TaskComposerTargetSelection>(
    () => (selectedRepo ? {kind: 'repository', repository: selectedRepo} : {kind: 'unselected'}),
    [selectedRepo],
  )
  const handleEnvironmentBarTargetSelection = useCallback(
    (selection: TaskComposerTargetSelection) => {
      setSelectedRepo(selection.kind === 'repository' ? selection.repository : undefined)
      if (selection.kind !== 'repository') setSelectedBranch(undefined)
    },
    [setSelectedBranch, setSelectedRepo],
  )

  const disabled = props.disabled || isAgentTaskSubmitInProgress || maxMessagesReached
  const interactionObserver = useCopilotClientInteractions({
    surface: 'chat_app',
    role: props.disabled || maxMessagesReached ? 'read_only' : 'writable',
    ...(isAhpEnvironment && selectedEnvironment?.environmentId
      ? // eslint-disable-next-line camelcase -- Preserve the canonical browser fact's binding vocabulary.
        {environment_id: selectedEnvironment.environmentId}
      : state.selectedThreadID
        ? // eslint-disable-next-line camelcase -- Preserve the canonical browser fact's binding vocabulary.
          {chat_id: state.selectedThreadID}
        : {}),
  })
  const interactionEventRef = useRef<Event | undefined>(undefined)
  useEffect(() => {
    const input = textAreaRef.current
    const container = containerRef.current
    if (!interactionObserver || !input || !container) return
    let stop: () => void
    try {
      stop = interactionObserver.observe(input, container, {
        commandFeedback: (kind, feedbackState, isVisible) => {
          const stopButton = container.querySelector<HTMLElement>('[data-testid="copilot-chat-stop-button"]')
          if (kind === 'stop') {
            return feedbackState.accepted && !getChatState().isWaitingOnCopilot && !input.readOnly && !stopButton
          }
          if (!isAhpEnvironment) return undefined
          return (
            feedbackState.pending &&
            getChatState().isWaitingOnCopilot &&
            input.value === '' &&
            Boolean(stopButton && isVisible(stopButton))
          )
        },
      })
    } catch {
      interactionObserver.reportError()
      return
    }
    const cleanupTimers = new Set<number>()
    const capture = (event: Event) => {
      interactionEventRef.current = event
      const timer = window.setTimeout(() => {
        cleanupTimers.delete(timer)
        if (interactionEventRef.current === event) interactionEventRef.current = undefined
      })
      cleanupTimers.add(timer)
    }
    container.addEventListener('click', capture, true)
    container.addEventListener('keydown', capture, true)
    return () => {
      stop()
      for (const timer of cleanupTimers) clearTimeout(timer)
      cleanupTimers.clear()
      interactionEventRef.current = undefined
      container.removeEventListener('click', capture, true)
      container.removeEventListener('keydown', capture, true)
    }
  }, [
    interactionObserver,
    textAreaRef,
    textareaPreviewRef,
    containerRef,
    getChatState,
    isAhpEnvironment,
    state.selectedThreadID,
  ])

  useImperativeHandle(props.chatInputRef, () => ({setText}) satisfies ChatInputRef, [setText])

  // Register this input with the manager so message-level actions (e.g. clickable suggestions) can pre-fill it
  // or submit through the surface's real submit path (which preserves shared-thread/quota/preview handling).
  useEffect(() => {
    const controller = {
      setText,
      focus: () => textAreaRef.current?.focus(),
      submit: (submitText: string) => {
        void wrappedOnSubmit(submitText)
      },
    }
    manager.registerChatInputController(controller)
    return () => manager.unregisterChatInputController(controller)
  }, [manager, setText, textAreaRef, wrappedOnSubmit])

  const emitChange = useSyntheticChange({inputRef: textAreaRef, fallbackEventHandler: handleChange})
  const {replaceUrlWithReferenceMention} = useAddUrlReferences({
    textAreaRef,
    emitChange,
  })
  const {
    isLoading: mentionReferencesPending,
    addReferenceForMention,
    addReferencesForMentions,
  } = useAddMentionReferences()
  const isCopilotSpacePath = useIsCopilotSpacePath()
  const isCopilotSpacePage = activeCopilotSpaceId && isCopilotSpacePath
  const preferredOverlaySide = useOverlayDirection()

  // Check if the current text is an incomplete slash command (memoized to avoid duplicate computation)
  const isIncompleteCommand = useMemo(() => isIncompleteSlashCommand(text, command), [text, command])
  const isAgent = isAgentCommand(currentMode?.name)
  const customModelSelectionPending =
    isAgent &&
    !isAhpEnvironment &&
    !!activeCommand?.requiresModelSelectionReady?.(getChatState()) &&
    !selectedAgentModelSelection.isReady

  const submitDisabled =
    slashCommandLoading ||
    mentionReferencesPending ||
    state.isWaitingOnAttachment ||
    !!props.submitDisabled ||
    (isAgent &&
      commandModes.includes(currentMode) &&
      (isAhpEnvironment
        ? !props.onEnvironmentSubmit ||
          !selectedEnvironment?.environmentId ||
          (isSandboxEnvironment && (!selectedRepo || isSandboxPolicyPending))
        : !selectedRepo && !selectedBranch && prettifiedBlocks.length === 0)) ||
    isAgentTaskSubmitInProgress ||
    isIncompleteCommand ||
    customModelSelectionPending

  const textAreaEmpty = text.length === 0

  const stopResponse = useCallback(
    (event: CommandEvent) => {
      interactionObserver?.command('stop', event.nativeEvent, 'not_applicable')
      const interaction = clientInteractionForEvent(event.nativeEvent)
      sendEvent('dotcom_chat.activate', {target: 'CHAT_MESSAGE_STOP', mode: state.mode})
      const stop = async () => {
        try {
          await handleStop(() => clientInteractionAccepted(interaction))
        } catch (error) {
          clientInteractionFailed(interaction)
          throw error
        }
      }
      void stop()
    },
    [handleStop, interactionObserver, state.mode],
  )

  const sendMessage = useCallback(
    (event: CommandEvent) => {
      if (submitDisabled) return
      const spaceIdString = activeCopilotSpaceId ? `${activeCopilotSpaceId.owner}/${activeCopilotSpaceId.id}` : ''
      sendEvent('dotcom_chat.activate', {
        target: 'CHAT_MESSAGE_SUBMIT',
        mode: state.mode,
        selectedThreadId: state.selectedThreadID,
        copilotSpace: spaceIdString,
        fromSpaceUI: isCopilotSpacePath,
        activeMode: currentMode.name,
        tokenBasedBillingEnabled,
        plan,
      })

      if (pastedFile) {
        clearPastedFile(false)
      }
      if (
        !disabled &&
        !props.isStreaming &&
        (text.trim() || prettifiedBlocks.length > 0 || hasSendableReferences || environmentAttachments.hasAttachments)
      ) {
        interactionObserver?.command('send', event.nativeEvent, isAhpEnvironment ? undefined : 'not_applicable')
      }
      void handleSubmit(undefined, event.nativeEvent)
    },
    [
      submitDisabled,
      disabled,
      activeCopilotSpaceId,
      state.mode,
      state.selectedThreadID,
      isCopilotSpacePath,
      currentMode.name,
      pastedFile,
      handleSubmit,
      clearPastedFile,
      tokenBasedBillingEnabled,
      plan,
      props.isStreaming,
      text,
      prettifiedBlocks.length,
      hasSendableReferences,
      environmentAttachments.hasAttachments,
      interactionObserver,
      isAhpEnvironment,
    ],
  )

  const sendMessageToNewThread = useCallback(() => {
    if (submitDisabled) return

    const interaction =
      !disabled && (text.trim() || hasSendableReferences)
        ? interactionObserver?.committedCommand('send', interactionEventRef.current)
        : undefined
    if (interaction) void observeLegacyCommand(interaction, handleSubmitToNewThread)
    else void handleSubmitToNewThread()
  }, [handleSubmitToNewThread, submitDisabled, disabled, text, hasSendableReferences, interactionObserver])

  const editLastMessage = useCallback(() => {
    // Skip user messages that recorded a confirmation response — the tool tied
    // to the confirmation has already executed, so editing the response would
    // misrepresent what happened.
    const lastEditableUserMessage = state.messages.findLast(
      m => m.role === 'user' && !(m.clientConfirmations && m.clientConfirmations.length > 0),
    )
    const id = lastEditableUserMessage?.id
    if (id) manager.startEditingMessage(id)
  }, [manager, state.messages])

  const placeholderText = (() => {
    if (state.isWaitingOnCopilot) return 'Copilot is responding…'
    if (state.defaultRecipient) return undefined
    if (maxMessagesReached) return 'Message limit reached. To continue chatting with Copilot, start a new conversation.'
    return currentMode.placeholder ?? 'Ask anything or type @ to add context'
  })()
  const defaultTextAreaAccessibleName = (() => {
    if (state.isWaitingOnCopilot) return 'Copilot is responding…'
    if (maxMessagesReached) {
      return 'Message limit reached. To continue chatting with Copilot, start a new conversation.'
    }
    return currentMode.textAreaAriaLabel || currentMode.placeholder || 'Ask anything or type @ to add context'
  })()
  const textAreaAccessibleName =
    props.formatTextAreaAriaLabel?.(defaultTextAreaAccessibleName) || defaultTextAreaAccessibleName

  if (state.defaultRecipient && text === '' && !typingHasStarted) {
    setText(`@${state.defaultRecipient} `)
  }

  const attachmentButtonRef = useRef<HTMLButtonElement>(null)

  const [attachmentMenuPanel, setAttachmentMenuPanel] = useState<AttachmentMenuPanelType | null>(null)
  const [lastAttachedRepo, setLastAttachedRepo] = useState<CopilotChatRepo | null>(null)
  const addAttachment = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU', mode: state.mode})
    setAttachmentMenuPanel(panel => (panel === 'attachment-types' ? null : 'attachment-types'))
  }, [state.mode, setAttachmentMenuPanel])

  // Image support is enabled in immersive and assistive modes.
  const showImagesByMode = state.mode === 'immersive' || state.mode === 'assistive'
  const shouldShowImages = (showImagesByMode && !!model.capabilities?.supports?.vision) || false

  const handleFileAttachment = useCallback(
    async (files: Iterable<File>, uploadType: 'drag' | 'paste', namePromise?: Promise<string>, name?: string) => {
      const imageAttacher = new CopilotImageAttacher(getChatState(), manager)
      const textAttacher = new CopilotTextAttacher(manager, props.existingFileNames)
      const attachedFiles: Array<ThreadScopedFileReference | ImageReference> = []

      const hasImageFiles = [...files].some(file => file.type.startsWith('image/'))
      if (hasImageFiles && !model.capabilities?.supports?.vision) {
        manager.addAmbientError(
          `The ${model.displayName} model doesn't support answering questions about images. Select a different model and try again.`,
        )
        sendVisionErrorEvent('vision_not_supported_for_model', {
          modelId: model.id,
          uploadType,
        })
      }

      // Split the image files out of the other files so we can handle them separately.
      const [imageFiles, otherFiles] = CopilotImageAttacher.getAllowedFiles([...files], model)

      if (shouldShowImages) {
        const imageAttachments = await imageAttacher.addImageAttachments(imageFiles, uploadType)
        if (imageAttachments) attachedFiles.push(...imageAttachments)
      }

      const disallowedFileTypes = []
      for (const file of otherFiles) {
        if (await CopilotTextAttacher.isAttachable(file)) {
          const attachedFile = await textAttacher.addAttachment(file, namePromise, name)
          if (attachedFile) attachedFiles.push(attachedFile)
        } else disallowedFileTypes.push(file.type)
      }

      if (shouldShowImages && disallowedFileTypes.length > 0) {
        sendVisionErrorEvent('included_unsupported_file', {
          fileTypes: disallowedFileTypes.join(','),
          uploadType,
        })
      }
      return attachedFiles
    },
    [shouldShowImages, model, getChatState, manager, props.existingFileNames],
  )

  const handleConvertToFile = useCallback(async () => {
    if (!pastedFile) return
    const fileArray = [pastedFile]
    const attachments = await handleFileAttachment(
      fileArray,
      'paste',
      pastedFileNamePromise.current || undefined,
      pastedFileName.current,
    )
    if (attachments.length > 0 && attachments[0]?.type === 'thread-scoped-file') {
      const pastedText = await pastedFile.text()
      const newText = text.replaceAll(pastedText, `@${attachments[0].name}`)
      setText(newText)
      clearPastedFile(false)
      sendEvent('dotcom_chat.activate', {target: 'CONVERT_TO_FILE_CONVERT', mode: state.mode})
    }
  }, [pastedFile, handleFileAttachment, state.mode, text, setText, clearPastedFile])

  const handleConvertToFileDismiss = useCallback(() => {
    clearPastedFile(true)
  }, [clearPastedFile])

  const handlePrettifiedBlockConvertToFile = useCallback(
    async (code: string, blockId: string) => {
      const file = new File([code], '', {type: CLIPBOARD_MIME_TYPE})
      const textAttacher = new CopilotTextAttacher(manager, props.existingFileNames)
      const namePromise = textAttacher.generateFileName(code, file.type)
      const attachments = await handleFileAttachment([file], 'paste', namePromise)
      if (attachments.length > 0) {
        const attachment = attachments[0]
        if (attachment?.type === 'thread-scoped-file') {
          setText(prev => (prev ? `${prev} @${attachment.name} ` : `@${attachment.name} `))
        }
        removePrettifiedBlock(blockId)
        sendEvent('dotcom_chat.activate', {target: 'PRETTIFY_CODE_CONVERT_TO_FILE', mode: state.mode})
      }
    },
    [manager, props.existingFileNames, handleFileAttachment, removePrettifiedBlock, setText, state.mode],
  )

  // Log when the convert attachment is shown
  useEffect(() => {
    if (pastedFile && !state.isWaitingOnAttachment && !convertToFileShownLogged.current) {
      convertToFileShownLogged.current = true
      sendEvent('dotcom_chat.activate', {target: 'CONVERT_TO_FILE_TRIGGER', mode: state.mode})
    }
  }, [pastedFile, state.isWaitingOnAttachment, state.mode])

  const supportsReferenceAttachments = currentMode.supportsReferenceAttachments ?? true
  const supportedReferenceTypes = currentMode.supportedReferenceTypes

  // AHP prompts carry attachments inline, so agent mode uses its own upload surface instead of
  // the cloud uploader that writes markdown image links into the textarea.
  const showEnvironmentAttachments = isAgent && isAhpEnvironment
  const environmentFileInputRef = useRef<HTMLInputElement>(null)
  const openEnvironmentFilePicker = useCallback(() => environmentFileInputRef.current?.click(), [])
  const handleEnvironmentFileDrop = useCallback(
    (files: FileList) => {
      void environmentAttachments.handleFilesAdded(Array.from(files), 'drop')
    },
    [environmentAttachments],
  )

  const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = useCallback(
    event => {
      const clipboardData = event.clipboardData
      if (!clipboardData?.items) return

      const fileArray: File[] = []

      // Batch DOM reads by converting to array first
      const items = Array.from(clipboardData.items)

      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file) fileArray.push(file)
        } else if (
          item.kind === 'string' &&
          ['text', 'text/plain'].includes(item.type) // "text" for tests, "text/plain" for real clipboard
        ) {
          const pastedText = clipboardData.getData(item.type)

          if (
            pastedText.length > 0 &&
            pastedText.length <= CopilotTextAttacher.getAttachmentSizeLimit() &&
            looksLikeCode(pastedText.slice(0, 5_000)) // should get about 100 lines of code
          ) {
            event.preventDefault()
            addPrettifiedBlock(pastedText)
            setCodeBlockStatus(`Block pasted, ${prettifiedBlocks.length + 1} total`)
            sendEvent('dotcom_chat.activate', {target: 'PRETTIFY_CODE_TRIGGER', mode: state.mode})
            return
          }

          if (
            pastedText.length > PASTE_TEXT_AS_FILE_THRESHOLD &&
            pastedText.length <= CopilotTextAttacher.getAttachmentSizeLimit()
          ) {
            // Run after the input value updates.
            safeSetTimeout(() => {
              convertToFileShownLogged.current = false

              const file = new File([pastedText], '', {type: CLIPBOARD_MIME_TYPE})
              const textAttacher = new CopilotTextAttacher(manager, props.existingFileNames)
              setPastedFile(file)
              const namePromise = textAttacher
                .generateFileName(pastedText, file.type)
                // eslint-disable-next-line github/no-then
                .then(name => (pastedFileName.current = name))
              pastedFileNamePromise.current = namePromise
            }, 0) // Use 0 instead of 1 for immediate scheduling
          } else if (supportsReferenceAttachments) {
            addReferencesForMentions(pastedText, supportedReferenceTypes)

            const insertionIndex = textAreaRef.current?.selectionStart ?? 0
            // After the paste completes, process the pasted text for URLs. Doing it this way (as opposed to preventing
            // the paste and setting the value synchronously) preserves the undo stack, allowing users to undo the
            // URL replacing if they want
            setTimeout(() =>
              replaceUrlWithReferenceMention(
                [insertionIndex, insertionIndex + pastedText.length],
                supportedReferenceTypes,
              ),
            )
          }
          return
        }
      }

      if (fileArray.length > 0) {
        event.preventDefault()
        if (showEnvironmentAttachments) {
          void environmentAttachments.handleFilesAdded(fileArray, 'paste')
        } else {
          void handleFileAttachment(fileArray, 'paste')
        }
      }
    },
    [
      safeSetTimeout,
      manager,
      props.existingFileNames,
      supportsReferenceAttachments,
      supportedReferenceTypes,
      addReferencesForMentions,
      textAreaRef,
      replaceUrlWithReferenceMention,
      handleFileAttachment,
      showEnvironmentAttachments,
      environmentAttachments,
      addPrettifiedBlock,
      prettifiedBlocks,
      state.mode,
    ],
  )

  // when the textarea is empty and blocks are present, backspace pops the last block
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (prettifiedBlocks.length === 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Backspace' && text === '') {
        event.preventDefault()
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        removePrettifiedBlock(prettifiedBlocks[prettifiedBlocks.length - 1]!.id)
        setCodeBlockStatus(`Block removed, ${prettifiedBlocks.length - 1} left`)
      }
    },
    [prettifiedBlocks, text, removePrettifiedBlock],
  )

  const handleFileDrop = useCallback(
    (files: FileList) => {
      void handleFileAttachment(files, 'drag')
    },
    [handleFileAttachment],
  )

  const onRepoAttach = useCallback(
    (repo: CopilotChatRepo | null) => {
      if (repo) {
        setLastUsedRepo(repo)
      }
      // Set last attached repo for multifile attach dialog, or clear out if null.
      setLastAttachedRepo(repo)
    },
    [setLastUsedRepo, setLastAttachedRepo],
  )

  const onCommandAutoComplete = useCallback(
    (commandKey: CopilotChatCommandType) => {
      const selectedCommand = availableCommands.find(cmd => cmd.key === commandKey)
      if (selectedCommand?.enabled?.() === false && selectedCommand.disabledAction) {
        selectedCommand.disabledAction(navigate)
        return
      }
      if (selectedCommand?.mode) {
        onModeSelect(selectedCommand.mode, true)

        // Clear the slash command text from the input
        const commandPattern = new RegExp(`^/${commandKey}\\s*`)
        if (commandPattern.test(text)) {
          setText(text.replace(commandPattern, ''))
        }
      } else if (currentMode.name !== ModeSupportName.Ask) {
        // Switch back to Ask mode if the selected command has no associated mode
        const askMode = chatModes.find(m => m.name === ModeSupportName.Ask)
        if (askMode) {
          onModeSelect(askMode, true)
        }
      }
    },
    [availableCommands, navigate, currentMode.name, onModeSelect, text, setText, chatModes],
  )

  const stoppable = props.isStreaming || state.isWaitingOnCopilot
  const supportsAttachments = Boolean(currentMode.supportsAttachments)
  const showCreateTaskStrip = isAgent && extraToolbar == null

  // Memoized so the `memo`-wrapped ChatInputToolbar isn't re-rendered by a new object
  // identity (and the agent TaskChatInputToolbar element isn't rebuilt) on every keystroke.
  const extraToolbarByMode = useMemo(
    () => ({
      default: extraToolbar,
      agent: extraToolbar ?? (
        <TaskChatInputToolbar
          selectedRepo={selectedRepo}
          selectedBranch={selectedBranch}
          selectedAgent={selectedAgent}
          onAgentSelect={agent => {
            setSelectedAgentModelSelection({isReady: false})
            setSelectedAgent(agent)
          }}
          agentSelectionEnabled={copilotFeatureFlags.sweAgentUseSubagents && !isAhpEnvironment}
          textAreaRef={textAreaRef}
          hideImageAttach={isAhpEnvironment}
          onAttachEnvironmentFiles={showEnvironmentAttachments ? openEnvironmentFilePicker : undefined}
          isUploadingEnvironmentFiles={environmentAttachments.isUploading}
          disabled={isAgentTaskSubmitInProgress}
        />
      ),
    }),
    [
      extraToolbar,
      isAhpEnvironment,
      showEnvironmentAttachments,
      openEnvironmentFilePicker,
      environmentAttachments.isUploading,
      selectedRepo,
      selectedBranch,
      selectedAgent,
      setSelectedAgent,
      setSelectedAgentModelSelection,
      textAreaRef,
      isAgentTaskSubmitInProgress,
    ],
  )

  return (
    <div className={styles.container}>
      <ScopedCommands
        commands={useMemo(
          () => ({
            // avoid hijacking the escape key unless there is some streaming to cancel
            'github:cancel': stoppable ? stopResponse : undefined,
            // When shouldSubmitWithModifier is true, use Mod+Enter; otherwise use Enter
            'copilot-chat:send-message': shouldSubmitWithModifier ? undefined : sendMessage,
            'github:submit-form': sendMessage,
            'copilot-chat:send-message-new-conversation': sendMessageToNewThread,
            'copilot-chat:add-attachment': addAttachment,
            'copilot-chat:edit-last-message': textAreaEmpty ? editLastMessage : undefined,
          }),
          [
            addAttachment,
            editLastMessage,
            sendMessage,
            sendMessageToNewThread,
            shouldSubmitWithModifier,
            stopResponse,
            stoppable,
            textAreaEmpty,
          ],
        )}
      >
        <form
          className={clsx(
            styles.chatForm,
            {
              [styles.containerSmall]: props.size === 'small', // Only used by assistive now
              [styles.containerAssistive]: state.mode === 'assistive',
              [styles.containerDisabled]: disabled,
              [styles.chatFormFocused]: isInputFocused,
            },
            props.className,
          )}
          ref={containerRef}
          {...testIdProps('copilot-chat-input-form')}
          {...props.dropTargetProps}
        >
          <ChatInputReferences
            className={styles.attachments}
            returnFocusRef={attachmentButtonRef}
            tokenSize={props.size === 'small' ? 'small' : 'medium'}
            isLoading={supportsReferenceAttachments && mentionReferencesPending}
            onSelectReference={onSelectReference}
            onPromoteImplicitReference={props.onPromoteImplicitReference}
            onRemoveReference={props.onRemoveReference}
            getReferenceVersion={props.getReferenceVersion}
            showConvertToFileText={supportsReferenceAttachments && !!pastedFile && !state.isWaitingOnAttachment}
            onConvertToFile={handleConvertToFile}
            onConvertToFileDismiss={handleConvertToFileDismiss}
            hideAttachmentsMenu={!!props.initialReference || currentMode.supportsAttachmentsMenu === false}
            referenceTypeFilter={supportedReferenceTypes}
          />

          {showEnvironmentAttachments && (
            <>
              {environmentAttachments.images.length > 0 && (
                <EnvironmentImageAttachments controller={environmentAttachments} />
              )}
              <EnvironmentAttachmentControls
                controller={environmentAttachments}
                fileInputRef={environmentFileInputRef}
              />
            </>
          )}

          {prettifiedBlocks.length > 0 && (
            <PrettifiedBlocksList
              blocks={prettifiedBlocks}
              supportsReferenceAttachments={supportsReferenceAttachments}
              onRemove={removePrettifiedBlock}
              onToggleCollapse={togglePrettifiedBlockCollapse}
              onCodeChange={updatePrettifiedBlockCode}
              onConvertToFile={isAgent ? undefined : handlePrettifiedBlockConvertToFile}
            />
          )}
          <div className="sr-only" role="status" aria-live="polite">
            {codeBlockStatus}
          </div>

          <ScopedCommands.LimitKeybindingScope
            // The default bindings for these are 'escape' and 'enter', so we need to strictly limit scope to avoid
            // breaking all the menu and button functionalities that rely on those keys
            commandIds={LIMITED_KEYBINDING_COMMAND_IDS}
            className={clsx(styles.inputContainer, {
              [styles.inputContainerNewConversation]: props.isNewConversation, // Only used by assistive now
              [styles.cssAutosize]: copilotFeatureFlags.cssTextareaAutosize,
            })}
            // CSS-autosize mode caps growth with an inline max-height; the legacy JS autosize applies
            // the override imperatively, so only set it here when the flag is on.
            style={
              copilotFeatureFlags.cssTextareaAutosize && props.maxTextareaHeight
                ? {maxHeight: props.maxTextareaHeight}
                : undefined
            }
            ref={textAreaPreviewContainerRef}
          >
            <ChatInputAutocomplete
              onSelectReference={addReferenceForMention}
              onSelectCommand={onCommandAutoComplete}
              hideAutocomplete={!currentMode.supportsAutocompletion}
              availableCommands={availableCommands}
              availableModes={chatModes}
              inputValue={text}
            >
              <textarea
                autoFocus={!props.disableAutoFocus}
                id={copilotChatTextAreaId}
                className={clsx(styles.input, {[styles.inputDisabled]: disabled})}
                autoComplete="off"
                autoCorrect={props.enableAutoCorrect ? 'on' : 'off'}
                aria-label={textAreaAccessibleName}
                aria-multiline="true"
                onPaste={props.onPaste ?? handlePaste}
                onKeyDown={handleKeyDown}
                ref={textAreaRef}
                onScroll={handleScroll}
                onChange={handleChangeWithAgentSwitch}
                onFocus={e => {
                  setIsInputFocused(true)
                  handleFocus(e)
                }}
                onBlur={() => setIsInputFocused(false)}
                placeholder={placeholderText}
                value={text}
                data-react-autofocus
                disabled={disabled}
                maxLength={props.maxPromptLength}
                {...testIdProps('copilot-chat-input-textarea')}
              />
            </ChatInputAutocomplete>
            <ChatInputPreview text={text} ref={textareaPreviewRef} availableCommandKeys={allCommandKeys} />
          </ScopedCommands.LimitKeybindingScope>
          <ChatInputToolbar
            addAttachment={addAttachment}
            currentMode={currentMode}
            mode={state.mode}
            attachmentButtonRef={attachmentButtonRef}
            modes={chatModes}
            onClickMode={onClickMode}
            stoppable={stoppable}
            submitDisabled={submitDisabled}
            isNewConversation={props.isNewConversation}
            onNewThreadSelected={props.onNewThreadSelected}
            supportsAttachments={supportsAttachments}
            disabled={disabled}
            shouldSubmitWithModifier={shouldSubmitWithModifier}
            extraToolbarByMode={extraToolbarByMode}
            chatCommand={command}
            isAssistive={state.mode === 'assistive'}
            isCopilotSpace={!!activeCopilotSpaceId}
            isAgentTaskSubmitInProgress={isAgentTaskSubmitInProgress}
            isIncompleteCommand={isIncompleteCommand}
            customModelPicker={customModelPicker}
            selectedAgent={selectedAgent}
            announcementElementRef={textAreaRef}
            onModelSelectionChange={setSelectedAgentModelSelection}
            showStartTaskButton={props.showStartTaskButton}
            availableCommands={availableCommands}
            navigate={navigate}
            forceIconOnly={props.forceIconOnly}
            isAhpAgentEnvironment={isAgent && isAhpEnvironment}
          />
        </form>
        {showCreateTaskStrip && (
          <EnvironmentStrip
            hideRepoSelector={isAhpEnvironment && !isSandboxEnvironment}
            initialRepoNWO={initialRepoNWO}
            initialBranch={selectedBranch}
            targetSelection={environmentStripTargetSelection}
            onTargetSelectionChange={handleEnvironmentBarTargetSelection}
            selectedBranch={selectedBranch}
            onBranchSelect={setSelectedBranch}
            currentPrompt={text}
            currentUserLogin={state.currentUserLogin}
            selectedEnvironment={selectedEnvironment}
            onSelectEnvironment={setSelectedEnvironment}
            showEnvironmentPicker={showEnvironmentPicker}
            includeSandboxOption={showSandboxOption}
            disabled={disabled}
          />
        )}
        {props.showStartTaskButton && (
          <SubmitButton
            disabled={submitDisabled}
            label={currentMode.submitLabel}
            isAgentTaskSubmitInProgress={isAgentTaskSubmitInProgress}
            isIncompleteCommand={isIncompleteCommand}
            showStartTaskButton={props.showStartTaskButton}
            shouldSubmitWithModifier={shouldSubmitWithModifier}
            className={clsx(styles.submitButtonOutsideForm)}
          />
        )}
      </ScopedCommands>
      <AttachmentMenu
        inputRef={textAreaRef}
        inputOnChange={handleChange}
        anchorRef={attachmentButtonRef}
        panel={attachmentMenuPanel}
        onPanelChange={setAttachmentMenuPanel}
        initialRepo={lastAttachedRepo}
        onRepoSelect={onRepoAttach}
        side={props.attachmentMenuSide ?? preferredOverlaySide}
      />

      {/*
        Do not add drag-and-drop handlers for ChatInput on a Copilot Space's edit page
        because they would interfere with the page's Spaces Resources attachment handlers.
      */}
      {supportsAttachments && !isCopilotSpacePage && (
        <FullScreenDragOverlay
          onFilesDrop={handleFileDrop}
          isUploading={state.isWaitingOnAttachment && state.attachmentProcessingType === 'file-upload'}
          defaultPreventedDropTargetRef={containerRef}
        />
      )}
      {showEnvironmentAttachments && !isCopilotSpacePage && (
        <FullScreenDragOverlay
          onFilesDrop={handleEnvironmentFileDrop}
          isUploading={environmentAttachments.isUploading}
          defaultPreventedDropTargetRef={containerRef}
        />
      )}
    </div>
  )
}

// `ChatInput` is large and rendered by parents (chat surfaces, task inputs, spaces, etc.) that
// re-render for unrelated reasons. Memoize it so a parent re-render with shallow-equal props is
// skipped. All dynamic data is read via hooks/context (useChatStateValues, useEntitlement,
// useChatManager, useChatInput, …), which re-render through `memo`, so this only blocks
// parent-prop-driven re-renders, not state/context updates.
export const ChatInput = memo(ChatInputImpl)
ChatInput.displayName = 'ChatInput'

// Toolbar

interface ChatInputToolbarProps {
  addAttachment: () => void
  attachmentButtonRef: RefObject<HTMLButtonElement | null>
  stoppable: boolean
  submitDisabled: boolean
  onNewThreadSelected?: () => Promise<void>
  extraToolbarByMode?: Record<string, React.ReactNode>
  currentMode: ModeSupport
  /** Chat mode reported in the MeterButton token-usage telemetry event. */
  mode?: string
  modes: ModeSupport[]
  isNewConversation?: boolean
  onClickMode?: (modeName: string) => void
  chatCommand: CopilotChatCommandType | null
  supportsAttachments: boolean
  disabled?: boolean
  isAssistive?: boolean
  isCopilotSpace?: boolean | null
  isAgentTaskSubmitInProgress?: boolean
  isIncompleteCommand: boolean
  customModelPicker?: (props: CustomModelPickerProps) => React.ReactNode
  selectedAgent?: SelectedAgent
  announcementElementRef?: CustomModelPickerProps['announcementElementRef']
  onModelSelectionChange?: CustomModelPickerProps['onModelSelectionChange']
  showStartTaskButton?: boolean
  availableCommands: AvailableCommand[]
  navigate: ReturnType<typeof useNavigate>
  shouldSubmitWithModifier?: boolean
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
  /** True while an AHP environment is selected in agent mode. Hides cloud-only controls. */
  isAhpAgentEnvironment?: boolean
}

const extraToolbarByModeDefault = {default: null, agent: null}

/** Maps overlay anchor side to tooltip direction */
const getTooltipDirection = (overlaySide: AnchorSide): 'n' | 's' => (overlaySide === 'outside-top' ? 'n' : 's')

// Toolbar width (px) below which the reasoning effort picker collapses to its icon-only
// form. Measured against the toolbar's own (parent-driven) width so it reflects the actual
// ChatInput size rather than the viewport, and can't flip-flop as the picker collapses.
const REASONING_PICKER_COLLAPSE_WIDTH = 480

const ChatInputToolbar = memo((props: ChatInputToolbarProps) => {
  const {
    chatCommand,
    extraToolbarByMode = extraToolbarByModeDefault,
    currentMode,
    mode: meterMode,
    supportsAttachments,
    submitDisabled,
    isAssistive,
    isCopilotSpace,
    isAgentTaskSubmitInProgress = false,
    isIncompleteCommand,
    showStartTaskButton,
    availableCommands,
    navigate,
    shouldSubmitWithModifier = false,
    customModelPicker,
    selectedAgent,
    announcementElementRef,
    onModelSelectionChange,
    onNewThreadSelected,
    disabled,
    isNewConversation,
    forceIconOnly,
    addAttachment,
    attachmentButtonRef,
    stoppable,
    modes,
    onClickMode,
    isAhpAgentEnvironment = false,
  } = props
  const {canUpgradePlan, tokenBasedBillingEnabled} = useEntitlement()
  const preferredOverlaySide = useOverlayDirection()
  const tooltipDirection = getTooltipDirection(preferredOverlaySide)
  const commandSupportsModelPickerAndMcp = chatCommand !== 'loop' && chatCommand !== 'spark'

  const showModelPicker = !!(
    onNewThreadSelected &&
    currentMode.supportsModelSwitching &&
    commandSupportsModelPickerAndMcp
  )

  const isAgent = isAgentCommand(currentMode.name)
  const attachmentButtonLabel = isCopilotSpace ? 'Add files' : 'Add files, and spaces'

  // Primer's narrow viewport range
  const isNarrow = useMediaQuery('(max-width: calc(48rem - 0.02px))')
  // Show icon only on narrow screens, after the conversation has started, or when forced by parent
  const showIconOnly = isNarrow || !isNewConversation || forceIconOnly

  // The reasoning effort picker keeps its text label whenever the toolbar has room, and
  // only collapses to an icon when the toolbar itself shrinks too narrow (or a parent forces
  // it) — independent of viewport size or conversation state. The shared resize-observer hook
  // coalesces notifications into a single rAF flush, and we bail on unchanged values, so this
  // can't drive a ResizeObserver-loop reflow (Safari/WebKit presentation stall).
  const [isToolbarNarrow, setIsToolbarNarrow] = useState(false)
  const toolbarRef = useResizeObserver(entry => {
    const width = entry.contentRect.width
    if (!width) return
    const next = width < REASONING_PICKER_COLLAPSE_WIDTH
    setIsToolbarNarrow(prev => (prev === next ? prev : next))
  })
  const reasoningPickerIconOnly = forceIconOnly || isToolbarNarrow

  return (
    <div
      ref={toolbarRef}
      className={clsx(styles.toolbar, {
        [styles.toolbarNoModes]: modes.length === 1,
      })}
    >
      {/* Modes */}
      {modes.length > 1 && (
        <div className={styles.mode}>
          <div className="d-flex">
            <ActionMenu>
              {showIconOnly ? (
                <ActionMenu.Anchor>
                  <IconButton
                    aria-label={currentMode.name}
                    className={styles.modeSelectButton}
                    variant="invisible"
                    icon={currentMode.icon}
                    tooltipDirection={tooltipDirection}
                  />
                </ActionMenu.Anchor>
              ) : (
                <ActionMenu.Button
                  className={styles.modeSelectButton}
                  leadingVisual={<currentMode.icon />}
                  variant="invisible"
                  trailingAction={TriangleDownIcon}
                >
                  {currentMode.name}
                </ActionMenu.Button>
              )}
              <ActionMenu.Overlay
                width="auto"
                side={preferredOverlaySide}
                position={isAssistive ? 'relative' : undefined}
                portalContainerName={isAssistive ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
              >
                <ActionList selectionVariant="single" role="menu" aria-label="Item">
                  {modes.map(mode => {
                    // Find the command associated with this mode
                    const modeCommand = availableCommands.find(cmd => cmd.mode?.name === mode.name)
                    // Check if command is disabled
                    let isCommandDisabled = false
                    if (modeCommand?.enabled) {
                      isCommandDisabled = modeCommand.enabled() === false
                    }
                    return (
                      <ActionList.Item
                        selected={currentMode.name === mode.name}
                        onSelect={() => {
                          if (isCommandDisabled && modeCommand?.disabledAction) {
                            modeCommand.disabledAction(navigate)
                          } else {
                            onClickMode?.(mode.name)
                          }
                        }}
                        key={mode.name}
                      >
                        <ActionList.LeadingVisual>{<mode.icon />}</ActionList.LeadingVisual>
                        {mode.name}
                        {isCommandDisabled && canUpgradePlan && (
                          <ActionList.TrailingVisual>
                            <Label>Upgrade</Label>
                          </ActionList.TrailingVisual>
                        )}
                      </ActionList.Item>
                    )
                  })}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
      )}

      <div className={styles.extra}>
        {supportsAttachments && !isCopilotSpace && (
          <ChatReposPicker showIconOnly={showIconOnly} tooltipDirection={tooltipDirection} />
        )}
        {supportsAttachments && (
          <IconButton
            ref={attachmentButtonRef}
            icon={PlusIcon}
            className={styles.attachmentIconButton}
            aria-label={attachmentButtonLabel}
            tooltipDirection={tooltipDirection}
            onClick={addAttachment}
            data-testid="attachment-menu-button"
          />
        )}
        {extraToolbarByMode[isAgent ? 'agent' : 'default']}
      </div>
      <div className={clsx(styles.actions, {[styles.actionsMultiple]: showModelPicker})}>
        {showModelPicker && (
          <ChatModelControls
            customModelPicker={customModelPicker}
            selectedAgent={selectedAgent}
            iconOnly={reasoningPickerIconOnly}
            announcementElementRef={announcementElementRef}
            onModelSelectionChange={onModelSelectionChange}
            onNewThreadSelected={onNewThreadSelected}
            disabled={disabled}
            isAhpEnvironment={isAhpAgentEnvironment}
          />
        )}
        {tokenBasedBillingEnabled && !isAhpAgentEnvironment && <MeterButton mode={meterMode} />}
        <div className={styles.toolbarButtons}>
          {stoppable ? (
            <StopButton tooltipDirection={tooltipDirection} />
          ) : (
            <SubmitButton
              disabled={submitDisabled}
              label={currentMode.submitLabel}
              isAgentTaskSubmitInProgress={isAgentTaskSubmitInProgress}
              isIncompleteCommand={isIncompleteCommand}
              showStartTaskButton={showStartTaskButton}
              shouldSubmitWithModifier={shouldSubmitWithModifier}
              tooltipDirection={tooltipDirection}
              className={showStartTaskButton ? styles.submitButtonInForm : undefined}
            />
          )}
        </div>
      </div>
    </div>
  )
})
ChatInputToolbar.displayName = 'ChatInputToolbar'

function SubmitButton({
  disabled,
  label,
  isAgentTaskSubmitInProgress = false,
  isIncompleteCommand,
  showStartTaskButton,
  shouldSubmitWithModifier = false,
  tooltipDirection = 's',
  className,
}: {
  disabled?: boolean
  label?: string
  isAgentTaskSubmitInProgress?: boolean
  isIncompleteCommand: boolean
  showStartTaskButton?: boolean
  shouldSubmitWithModifier?: boolean
  tooltipDirection?: 'n' | 's'
  className?: string
}) {
  // Determine the appropriate aria-label based on why the button is disabled
  const getAriaLabel = () => {
    if (!disabled) {
      return label || 'Send now'
    }

    // Check if it's disabled because of an incomplete slash command
    if (isIncompleteCommand) {
      return 'Complete your slash command before sending'
    }

    return label || 'Sending a message is unavailable'
  }

  const buttonContent = isAgentTaskSubmitInProgress ? (
    showStartTaskButton ? (
      <CommandButton
        commandId={shouldSubmitWithModifier ? 'github:submit-form' : 'copilot-chat:send-message'}
        leadingVisual={PaperAirplaneIcon}
        loading
        loadingAnnouncement="Creating task"
      >
        {START_TASK_BUTTON_TEXT}
      </CommandButton>
    ) : (
      <CommandIconButton
        commandId={shouldSubmitWithModifier ? 'github:submit-form' : 'copilot-chat:send-message'}
        variant="invisible"
        size="medium"
        icon={PaperAirplaneIcon}
        aria-label={getAriaLabel()}
        tooltipDirection={tooltipDirection}
        loading
        loadingAnnouncement="Creating task"
      />
    )
  ) : showStartTaskButton && disabled ? (
    <Button size="medium" leadingVisual={PaperAirplaneIcon} inactive aria-disabled>
      {START_TASK_BUTTON_TEXT}
    </Button>
  ) : disabled ? (
    <IconButton
      variant="invisible"
      size="medium"
      icon={PaperAirplaneIcon}
      aria-label={getAriaLabel()}
      tooltipDirection={tooltipDirection}
      inactive
      aria-disabled
    />
  ) : showStartTaskButton ? (
    <CommandButton
      commandId={shouldSubmitWithModifier ? 'github:submit-form' : 'copilot-chat:send-message'}
      leadingVisual={PaperAirplaneIcon}
    >
      {START_TASK_BUTTON_TEXT}
    </CommandButton>
  ) : (
    <CommandIconButton
      commandId={shouldSubmitWithModifier ? 'github:submit-form' : 'copilot-chat:send-message'}
      variant="invisible"
      size="medium"
      icon={PaperAirplaneIcon}
      aria-label={getAriaLabel()}
      tooltipDirection={tooltipDirection}
    />
  )

  return className ? <div className={className}>{buttonContent}</div> : buttonContent
}

function StopButton({tooltipDirection}: {tooltipDirection: 'n' | 's'}) {
  return (
    <CommandIconButton
      {...testIdProps('copilot-chat-stop-button')}
      commandId="github:cancel"
      variant="invisible"
      size="medium"
      icon={SquareFillIcon}
      tooltipDirection={tooltipDirection}
      className={styles.CommandIconButton}
    />
  )
}
